import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';

import { useGlobal } from '~/hooks/useGlobal';
import type { Category } from '~/stores/CategoriesStore';

import { PromoCountdown, usePromoCountdown } from './PromoCountdown';
import { CategoryBlockTitle } from './shared/CategoryBlockTitle';
import { ProductsCarouselWithBackground } from './shared/ProductsCarouselWithBackground';

interface ProductsCarouselSliderTimerProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  category: Category;
  priority?: boolean;
}

const MOBILE_ASPECT_RATIO = 355 / 431;
const DESKTOP_ASPECT_RATIO = 1400 / 430;

const mobileClass = 'overflow-hidden rounded-6.5 bg-graphic-regular-brand p-4';
const carouselClass =
  'p-4 pb-8 bg-graphic-regular-brand overflow-hidden rounded-6.5';

export const ProductsCarouselSliderTimer: React.FC<
  ProductsCarouselSliderTimerProps
> = ({ category, ...props }) => {
  const { isMobile } = useGlobal();
  const { isExpired, remainingMs, hasEndDate } = usePromoCountdown(
    category.promoEndDate,
  );

  if (isExpired) {
    return null;
  }

  const description = category.description;
  const pill =
    remainingMs !== null ? <PromoCountdown remainingMs={remainingMs} /> : null;

  const intro = isMobile ? (
    <div className="flex min-h-32 flex-col gap-3 pb-3">
      <div
        className={cn(
          'flex items-center justify-between gap-2',
          hasEndDate && 'min-h-9',
        )}
      >
        <CategoryBlockTitle inverse>{category.name}</CategoryBlockTitle>
        {pill}
      </div>
      {description && (
        <Typography.Header300Regular
          as="p"
          className="whitespace-pre-line text-center text-text-primary-inverse"
        >
          {description}
        </Typography.Header300Regular>
      )}
    </div>
  ) : description || hasEndDate ? (
    <div className="grid min-h-46 grid-cols-[1fr_auto_1fr] items-start gap-4 px-2 pt-2 pb-12">
      {description && (
        <Typography.Header500Regular
          as="p"
          className="col-start-2 mt-4! whitespace-pre-line text-center text-text-primary-inverse"
        >
          {description}
        </Typography.Header500Regular>
      )}
      {pill && <div className="col-start-3 justify-self-end">{pill}</div>}
    </div>
  ) : null;

  return (
    <ProductsCarouselWithBackground
      {...props}
      category={category}
      mobileAspectRatio={MOBILE_ASPECT_RATIO}
      desktopAspectRatio={DESKTOP_ASPECT_RATIO}
      mobileClassName={mobileClass}
      carouselClassName={carouselClass}
      intro={intro}
      mobileImage={category.previewImageThumb}
    />
  );
};
